import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://thenextcm.com";

/**
 * Lightweight SEO/AEO head manager for SPA routes.
 * Updates document.title + key meta tags + JSON-LD on route change.
 *
 * Usage: <SeoHead title="..." description="..." canonical="/m/abc" schema={ ... } />
 */
export const SeoHead = ({
  title,
  description,
  canonical,
  ogImage = "/og-kerala-speaks.png?v=3",
  ogType = "website",
  schema, // JSON-LD object or array
  noindex = false,
}) => {
  const location = useLocation();

  useEffect(() => {
    if (title) document.title = title;

    const fullUrl = canonical
      ? (canonical.startsWith("http") ? canonical : SITE_URL + canonical)
      : SITE_URL + location.pathname;
    const fullOgImage = ogImage.startsWith("http") ? ogImage : SITE_URL + ogImage;

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    upsertLink("canonical", fullUrl);

    // Open Graph
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:url", fullUrl);
    upsertMeta("property", "og:image", fullOgImage);

    // Twitter
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", fullOgImage);

    // JSON-LD schema for AEO
    const tag = document.getElementById("__seo_jsonld");
    if (schema) {
      const json = Array.isArray(schema)
        ? { "@context": "https://schema.org", "@graph": schema }
        : (schema["@context"] ? schema : { "@context": "https://schema.org", ...schema });
      if (tag) {
        tag.textContent = JSON.stringify(json);
      } else {
        const s = document.createElement("script");
        s.id = "__seo_jsonld";
        s.type = "application/ld+json";
        s.textContent = JSON.stringify(json);
        document.head.appendChild(s);
      }
    } else if (tag) {
      tag.remove();
    }
  }, [title, description, canonical, ogImage, ogType, schema, noindex, location.pathname]);

  return null;
};

function upsertMeta(attr, key, value) {
  if (!value) return;
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", value);
}

function upsertLink(rel, href) {
  if (!href) return;
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

export default SeoHead;
